/**
 * Conta internacional FX convert + outbound transfer via IB account proxy:
 * POST /v1/ib/accounts/fx/quotes → BaaS /v1/accounts/{id}/fx/quotes
 * POST /v1/ib/accounts/fx/conversions → BaaS /v1/accounts/{id}/fx/conversions
 * POST /v1/ib/accounts/transfers → BaaS /v1/accounts/{id}/transfers
 */

import { fetchAxios } from "../API/fetchAxios";

const toMinorUnits = (majorAmount, currencyCode) => {
  const code = String(currencyCode || "").toUpperCase();
  const decimals = code === "JPY" ? 0 : 2;
  const n = Number(majorAmount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 10 ** decimals);
};

const fromMinorUnits = (minorAmount, currencyCode) => {
  const code = String(currencyCode || "").toUpperCase();
  const decimals = code === "JPY" ? 0 : 2;
  const n = Number(minorAmount);
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** decimals;
};

const extractError = (error) => {
  const data = error?.response?.data ?? error?.data ?? {};
  return {
    status: error?.response?.status ?? error?.status ?? null,
    code: data?.error || data?.title || "request_failed",
    detail: data?.detail || data?.message || error?.message || "Request failed.",
  };
};

export const createIbFxQuote = async ({ fromCurrency, toCurrency, amountMajor }) => {
  const from = String(fromCurrency || "").toUpperCase();
  const to = String(toCurrency || "").toUpperCase();
  const amount = toMinorUnits(amountMajor, from);
  if (!from || !to || amount <= 0) {
    throw Object.assign(new Error("invalid_fx_quote"), {
      response: { status: 400, data: { error: "validation_error", detail: "Invalid FX quote request." } },
    });
  }

  try {
    const response = await fetchAxios({
      verb: "POST",
      context: "accountFxQuote",
      data: {
        from_currency: from,
        to_currency: to,
        amount,
      },
      responseType: "",
      cancelToken: undefined,
    });
    const data = response?.data ?? response;
    return {
      quoteId: data?.quote_id,
      fromCurrency: data?.from_currency,
      toCurrency: data?.to_currency,
      rate: Number(data?.rate) || 0,
      fromAmountMajor: fromMinorUnits(data?.from_amount, data?.from_currency),
      toAmountMajor: fromMinorUnits(data?.to_amount, data?.to_currency),
      feeAmountMajor: fromMinorUnits(data?.fee_amount, data?.fee_currency || data?.to_currency),
      feePercent: Number(data?.fee_percent) || 0,
      source: data?.source,
      expiresAt: data?.expires_at,
      raw: data,
    };
  } catch (error) {
    throw Object.assign(error instanceof Error ? error : new Error("fx_quote_failed"), {
      baas: extractError(error),
    });
  }
};

export const createIbFxConversion = async ({
  quoteId,
  fromCurrency,
  toCurrency,
  amountMajor,
}) => {
  const body = quoteId
    ? { quote_id: quoteId }
    : {
        from_currency: String(fromCurrency || "").toUpperCase(),
        to_currency: String(toCurrency || "").toUpperCase(),
        amount: toMinorUnits(amountMajor, fromCurrency),
      };

  try {
    const response = await fetchAxios({
      verb: "POST",
      context: "accountFxConversion",
      data: body,
      responseType: "",
      cancelToken: undefined,
    });
    return response?.data ?? response;
  } catch (error) {
    throw Object.assign(error instanceof Error ? error : new Error("fx_conversion_failed"), {
      baas: extractError(error),
    });
  }
};

export const createIbOutboundTransfer = async ({
  currencyCode,
  amountMajor,
  scheme,
  beneficiary,
  reference,
}) => {
  const currency = String(currencyCode || "").toUpperCase();
  const amount = toMinorUnits(amountMajor, currency);
  try {
    const response = await fetchAxios({
      verb: "POST",
      context: "accountOutboundTransfer",
      data: {
        currency_code: currency,
        amount,
        scheme,
        beneficiary,
        reference: reference || undefined,
      },
      responseType: "",
      cancelToken: undefined,
    });
    return response?.data ?? response;
  } catch (error) {
    throw Object.assign(error instanceof Error ? error : new Error("transfer_failed"), {
      baas: extractError(error),
    });
  }
};

export const formatBaasErrorMessage = (error) => {
  const baas = error?.baas || extractError(error);
  if (baas.code === "transfer_partner_unavailable") {
    return "Transferência internacional ainda não disponível neste ambiente (parceiro bancário não configurado).";
  }
  if (baas.code === "fx_rate_unavailable" || baas.code === "fx_quote_expired" || baas.code === "fx_quote_not_found") {
    return baas.detail || "Cotação de câmbio indisponível. Tente novamente.";
  }
  if (baas.code === "insufficient_balance") {
    return "Saldo insuficiente para concluir a operação.";
  }
  if (baas.status === 404 || baas.code === "account_not_found") {
    return "Conta ou endpoint BaaS não encontrado. Publique o BaaS com as rotas de FX/transfers.";
  }
  return baas.detail || "Não foi possível concluir a operação.";
};
